import { Stack, Typography } from '@mui/material';
import { MgItemCard } from '@retail/products/types';
import { useProductsTFunction } from '@retail/products/i18n';
import { useIsSuperUser } from '@retail/users/data-access';
import {
  AddToMonitoringAssortmentMenu,
  MgItemView,
  SuccessAddingItemsDialog,
} from '@retail/products/components';
import { useToast } from '@retail/hooks';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useCallback, useMemo, useState } from 'react';
import { MonitoringAssortment } from '@retail/monitoring-assortment/types';
import { useDisclosure } from '@shared/hooks';
import { useMonitoringAssortmentActions } from '@retail/monitoring-assortment/context';
import {
  CreateMonitoringAssortmentDialog,
  NewAssortment,
} from '@retail/monitoring-assortment/components';
import { FetchError } from '@shared/fetch-utils';
import { AddButton, SuspenseWithSentryErrorBoundary } from '@retail/components';
import { TagHeading } from '@shared/components';
import { MgItemGallery } from './MgItemGallery';
import { useNavigate } from 'react-router';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { useFetchAssortmentsForMgItem } from '@retail/products/data-access';
import { SupplierItemsDetails } from './SupplierItemsDetails';

interface Props {
  mgItem: MgItemCard;
  assortmentId: number;
  monitoringAssortments: MonitoringAssortment[];
  showMonitorItem?: boolean;
  showAddItem?: boolean;
  openSelectItemModal: () => void;
}

export function MgItemDetails({
  mgItem,
  assortmentId,
  monitoringAssortments,
  showMonitorItem = true,
  showAddItem = true,
  openSelectItemModal,
}: Props) {
  const t = useProductsTFunction();
  const isSuperUser = useIsSuperUser();
  const toast = useToast();
  const navigate = useNavigate();

  const { selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const { userCanCreate, userCanRead } = usePermissionsForCurrentUser();
  const canAddAssortmentItems = userCanCreate('ASSORTMENT_ITEMS') && showAddItem;
  const canReadExtendedItemInfo = userCanRead('EXTENDED_MG_ITEM_INFO');
  const canAddMonitoringAssortmentItems =
    userCanCreate('MONITORING_ASSORTMENT_ITEMS') && showMonitorItem;

  const navigateToMonitoringAssortment = useCallback(
    (assortmentId: number) => {
      navigate(mgPrisRoutes.marketInsight.assortments.assortment.getFullLinkPath({ assortmentId }));
    },
    [navigate]
  );

  const firstSupplierItem = mgItem.getFirstSupplierItem();

  const { data: mgItemAssortmentDtos = [] } = useFetchAssortmentsForMgItem(
    { mgItemNumber: mgItem.mgItemNumber, orgUnitId: selectedOrgUnitAssortment.orgUnitId },
    { suspense: false }
  );

  const monitoringAssortmentIds = useMemo(
    () => mgItemAssortmentDtos.map((assortment) => assortment.id),
    [mgItemAssortmentDtos]
  );

  const [selectedAssortment, setSelectedAssortment] = useState<MonitoringAssortment | null>(null);
  const [successModal, setSuccessModal] = useState<boolean>(false);

  const {
    isOpen: isCreateMonitoringAssortmentOpen,
    onClose: closeCreateMonitoringAssortment,
    onOpen: openCreateMonitoringAssortment,
  } = useDisclosure(false);

  const {
    handleCreateMonitoringAssortment,
    addItemsToAssortment,
    deleteItemsFromAssortment,
    isLoading,
  } = useMonitoringAssortmentActions();

  const submitNewAssortmentForm = ({ title }: NewAssortment) => {
    handleCreateMonitoringAssortment(
      { title, itemCodes: { mgItemNumbers: [mgItem.mgItemNumber] } },
      selectedOrgUnitAssortment.orgUnitId
    )
      .then((assortment) => setSelectedAssortment(assortment))
      .catch((err: FetchError) => {
        console.error(err);
        toast.error(t('products.mgItem.search.error.addingItemToAssortment'));
      });
  };

  const handleItemSuccess = useCallback(
    (assortmentId: number) => {
      const currentAssortment =
        monitoringAssortments.find((assortment) => assortment.id === assortmentId) ?? null;
      setSelectedAssortment(currentAssortment);
    },
    [monitoringAssortments]
  );

  const updateItemInAssortment = useCallback(
    (assortmentId: number) => {
      monitoringAssortmentIds.includes(assortmentId)
        ? deleteItemsFromAssortment({ assortmentId, mgItemNumbers: [mgItem.mgItemNumber] })
        : addItemsToAssortment({ assortmentId, mgItemNumbers: [mgItem.mgItemNumber] })
            .then(() => {
              setSuccessModal(true);
              handleItemSuccess(assortmentId);
            })
            .catch(() => toast.error(t('products.errors.addItemToAssortment')));
    },
    [
      addItemsToAssortment,
      deleteItemsFromAssortment,
      handleItemSuccess,
      mgItem.mgItemNumber,
      monitoringAssortmentIds,
      t,
      toast,
    ]
  );

  const handleNavigateToAssortment = () => {
    navigateToMonitoringAssortment(selectedAssortment!.id);
  };

  return (
    <Stack flexGrow={1} gap={1} p={3} pt={1} overflow="hidden">
      <Stack
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        pb={1}
        whiteSpace="nowrap"
      >
        <TagHeading color="primary">
          <Typography variant="h4">{firstSupplierItem.primaryText}</Typography>
        </TagHeading>

        <Stack flexDirection="row" gap={1}>
          {canAddMonitoringAssortmentItems && (
            <AddToMonitoringAssortmentMenu
              availableMonitoringAssortments={monitoringAssortments}
              monitoringAssortmentIds={monitoringAssortmentIds}
              updateItemInAssortment={updateItemInAssortment}
              onAddToNewMonitoringAssortments={openCreateMonitoringAssortment}
            />
          )}
          {canAddAssortmentItems && (
            <AddButton onClick={openSelectItemModal}>
              {t('products.mgItem.supplierItem.addItem')}
            </AddButton>
          )}
        </Stack>
      </Stack>
      <Stack display="grid" gap={2} gridTemplateColumns="0.3fr 0.7fr" overflow="hidden">
        <Stack gap={6} overflow="auto">
          <MgItemView mgItem={mgItem} isSuperUser={isSuperUser} />
          {canReadExtendedItemInfo && (
            <SuspenseWithSentryErrorBoundary>
              <MgItemGallery mgItemNumber={mgItem.mgItemNumber} />
            </SuspenseWithSentryErrorBoundary>
          )}
        </Stack>

        <Stack overflow="hidden">
          <SupplierItemsDetails key={mgItem.mgItemNumber} mgItem={mgItem} />
        </Stack>
      </Stack>

      <CreateMonitoringAssortmentDialog
        open={isCreateMonitoringAssortmentOpen}
        onClose={closeCreateMonitoringAssortment}
        onSubmit={submitNewAssortmentForm}
        navigateToAssortment={handleNavigateToAssortment}
        isLoading={isLoading}
        hideAddItemsLater
      />

      {successModal && (
        <SuccessAddingItemsDialog
          isOpen
          closeDialog={() => setSuccessModal(false)}
          successText={t('products.mgItem.addItemsToAssortment.added', {
            itemName: firstSupplierItem?.primaryText,
          })}
          assortmentId={selectedAssortment?.id ?? assortmentId}
          assortmentTitle={selectedAssortment?.title ?? t('products.mgItem.assortment')}
          navigate={handleNavigateToAssortment}
        />
      )}
    </Stack>
  );
}
